'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Head from 'next/head';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

interface CaseResult {
  hires: number;
  cph: number;
  tth: number;
  show: number;
}

interface CaseItem {
  id: string;
  title: string;
  service: string;
  industry: string;
  size: string;
  year: number;
  cover: string;
  result: CaseResult;
  badges: ('TOP' | 'AWARD')[];
  kpis: string[];
  summary: string;
  before: string[];
  after: string[];
}

const CASES_DATA: CaseItem[] = [
  {
    id: 'c1', title: 'Маркетплейс: 180 закрытий/кв.', service: 'Подбор (RPO)', industry: 'Торговля и маркетинг', size: 'Enterprise', year: 2025,
    cover: 'https://placehold.co/800x480?text=RPO+Marketplace',
    result: { hires: 180, cph: -19, tth: -27, show: 96 }, badges: ['TOP'],
    kpis: ['KPI', 'SLA', 'RPO'], summary: 'Выстроили воронку и коммуникации, внедрили BI-дашборды.',
    before: ['Нет единой воронки', 'Низкая явка', 'Долгие согласования'],
    after: ['180 закрытий за квартал', 'CPH −19%', 'Time-to-Hire −27%']
  },
  {
    id: 'c2', title: 'Call-center L1/L2: аутстаффинг', service: 'Аутстаффинг', industry: 'Сфера обслуживания', size: 'Mid', year: 2025,
    cover: 'https://placehold.co/800x480?text=Outstaff+Support',
    result: { hires: 65, cph: -12, tth: -20, show: 92 }, badges: ['AWARD'],
    kpis: ['QA', 'Обучение', 'Явка'], summary: 'Укомплектовали линию, запустили обучение и QA-контроль.',
    before: ['Нехватка операторов', 'Высокий no-show'], after: ['Штат +65 FTE', 'Явка 92%', 'Снижение очереди −35%']
  },
  {
    id: 'c3', title: 'Склад и логистика: аутсорсинг', service: 'Аутсорсинг', industry: 'Транспорт и логистика', size: 'Enterprise', year: 2025,
    cover: 'https://placehold.co/800x480?text=Outsource+Logistics',
    result: { hires: 220, cph: -15, tth: -18, show: 94 }, badges: [],
    kpis: ['SLA', 'MSP'], summary: 'Взяли на себя набор и управление сменами.',
    before: ['Сезонные пики', 'Высокий CPH'], after: ['220 FTE', 'CPH −15%', 'Стабильная явка 94%']
  },
  {
    id: 'c4', title: 'Интернет-магазин: RPO пилот', service: 'Подбор (RPO)', industry: 'IT-сфера и Интернет', size: 'Startup/SMB', year: 2025,
    cover: 'https://placehold.co/800x480?text=RPO+Pilot',
    result: { hires: 42, cph: -17, tth: -22, show: 90 }, badges: [],
    kpis: ['ATS', 'Слоты'], summary: 'Быстрый пилот, шаблоны коммуникаций, слоты интервью.',
    before: ['Разрозненные каналы', 'Нет SLA'], after: ['42 закрытия', 'TTH −22%', 'Шаблоны + автособеседования']
  },
  {
    id: 'c5', title: 'Техподдержка SaaS: аутсорсинг', service: 'Аутсорсинг', industry: 'IT-сфера и Интернет', size: 'Mid', year: 2025,
    cover: 'https://placehold.co/800x480?text=SaaS+Support',
    result: { hires: 90, cph: -21, tth: -25, show: 95 }, badges: ['TOP'],
    kpis: ['NPS', 'QA'], summary: 'Выделенная команда L1, скрипты, QA и NPS-контроль.',
    before: ['Высокая нагрузка', 'Нет скриптов'], after: ['NPS +14 п.п.', 'Сокращение TTH −25%', 'CPH −21%']
  },
];


const badgeClassName = (b: 'TOP' | 'AWARD') => b === 'TOP' ? 'badge-top' : b === 'AWARD' ? 'badge-awd' : 'tag';
const fmt = (n: number | string): string => typeof n === 'number' ? (n > 0 ? ('+' + n) : n) + '%' : String(n);
const impactScore = (c: CaseItem): number => {
  return (c.result?.hires || 0) + (-(c.result?.cph || 0)) * 2 + (-(c.result?.tth || 0)) * 1.5 + (c.result?.show || 0) / 10;
};
const caseUrl = (id: string) => `${window.location.origin}${window.location.pathname}#case-${id}`;

const PER_PAGE = 6;

interface LeadFormProps {
  source?: string;
}

const LeadForm: React.FC<LeadFormProps> = ({ source = 'Cases Page CTA' }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const endpoint = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/leads`;

  const resetForm = useCallback(() => {
    setName('');
    setEmail('');
    setIsConsentChecked(false);
    setStatus('success');
  }, []);

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsConsentChecked(e.target.checked);
    if (e.target.checked && status === 'error' && message.includes('согласитесь с политикой')) {
      setStatus('idle');
      setMessage('');
    }
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || status === 'success') return;
    if (!isConsentChecked) {
      setStatus('error');
      setMessage('❌ Пожалуйста, согласитесь с политикой конфиденциальности.');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setMessage('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: name,
            email: email,
            source: source,
            formName: "Кейсы INTERIM",
          },
        }),
      });

      console.log('Response status:', response.status);
      console.log('Request body:', JSON.stringify({ data: { name, email, source } }));

      if (response.ok) {
        setMessage('✅ Заявка успешно отправлена! Скоро свяжемся с вами.');
        resetForm();
      } else {
        const responseText = await response.text();
        console.error('Response text:', responseText);
        let errorMessage = 'Неизвестная ошибка сервера.';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error?.message || response.statusText || `Ошибка ${response.status}`;
        } catch (parseError) {
          console.error('Parse error:', parseError);
          errorMessage = `Ошибка сервера: ${response.status} - ${responseText.substring(0, 100)}...`;
        }
        setStatus('error');
        setMessage(`❌ Ошибка отправки: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Network or other error:', error);
      setStatus('error');
      setMessage('❌ Произошла ошибка сети. Попробуйте позже.');
    } finally {
      setIsSubmitting(false);
    }
  }, [name, email, source, isConsentChecked, isSubmitting, status, resetForm]);

  const buttonText = isSubmitting
    ? 'Отправка...'
    : status === 'success'
      ? '✅ Успешно'
      : 'Получить консультацию';

  const buttonClass = status === 'success'
    ? 'bg-green-500 hover:bg-green-600 text-white'
    : status === 'error'
      ? 'btn-ghost'
      : 'btn-accent';

  const StatusMessage: React.FC = () => {
    if (status === 'idle') return null;

    const className = status === 'success'
      ? 'text-green-400 bg-green-900/50'
      : 'text-red-400 bg-red-900/50';

    return (
      <p className={`mt-3 px-4 py-2 rounded-xl text-sm ${className} w-full`} role="alert">
        {message}
      </p>
    );
  };

  return (
    <>
      <form className="mt-4 grid gap-3 grid-cols-1 md:grid-cols-3" onSubmit={handleSubmit}>
        <input
          className="rounded-xl px-4 py-3 text-gray-900 w-full"
          placeholder="Имя*"
          value={name}
          onChange={(e) => { setName(e.target.value); setStatus('idle'); setMessage(''); }}
          required
          disabled={isSubmitting || status === 'success'}
        />
        <input
          className="rounded-xl px-4 py-3 text-gray-900 w-full"
          placeholder="E-mail*"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus('idle'); setMessage(''); }}
          required
          disabled={isSubmitting || status === 'success'}
        />
        <button
          className={`btn ${buttonClass} rounded-xl px-4 py-3 w-full`}
          type="submit"
          disabled={isSubmitting || name.trim() === '' || email.trim() === '' || !isConsentChecked}
        >
          {buttonText}
        </button>
        <div className="md:col-span-3 w-full">
          <CustomCheckbox
            name="consent"
            isChecked={isConsentChecked}
            onChange={handleConsentChange}
            link="/privacy"
            textColorClass="text-gray-400 text-xs"
            linkColorClass="text-[#FF7C22] hover:underline text-xs"
            className="px-2 py-3"
          />
        </div>

      </form>
      <StatusMessage />
    </>
  );
};

export default function CasesPage() {
  const [query, setQuery] = useState('');
  const [service, setService] = useState('');
  const [industry, setIndustry] = useState('');
  const [size, setSize] = useState('');
  const [yearMin, setYearMin] = useState(2025);
  const [yearMax, setYearMax] = useState(2026);
  const [sortBy, setSortBy] = useState('date');
  const [onlyTop, setOnlyTop] = useState(false);
  const [page, setPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCaseId, setModalCaseId] = useState<string | null>(null);

  const openModal = useCallback((id: string) => {
    setModalCaseId(id);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalCaseId(null);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, service, industry, size, yearMin, yearMax, sortBy, onlyTop]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isModalOpen, closeModal]);

  const filteredItems = useMemo(() => {
    let items = CASES_DATA.filter(c => {
      const q = query.toLowerCase();
      const hay = (c.title + ' ' + (c.summary || '') + ' ' + c.kpis.join(' ') + ' ' + c.service + ' ' + c.industry + ' ' + c.size).toLowerCase();

      const okQuery = !q || hay.includes(q);
      const okService = !service || c.service === service;
      const okIndustry = !industry || c.industry === industry;
      const okSize = !size || c.size === size;
      const okYear = c.year >= yearMin && c.year <= yearMax;
      const okTop = !onlyTop || c.badges.length > 0;

      return okQuery && okService && okIndustry && okSize && okYear && okTop;
    });

    switch (sortBy) {
      case 'date':
        items.sort((a, b) => b.year - a.year);
        break;
      case 'impact':
        items.sort((a, b) => impactScore(b) - impactScore(a));
        break;
      case 'title':
        items.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
        break;
    }
    return items;
  }, [query, service, industry, size, yearMin, yearMax, sortBy, onlyTop]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PER_PAGE));
  const start = (page - 1) * PER_PAGE;
  const end = start + PER_PAGE;
  const paginatedItems = filteredItems.slice(start, end);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [totalPages, page]);

  const statsAll = useMemo(() => CASES_DATA.reduce((acc, c) => ({
    hires: acc.hires + (c.result?.hires || 0),
    cph: acc.cph + (c.result?.cph || 0),
    tth: acc.tth + (c.result?.tth || 0)
  }), { hires: 0, cph: 0, tth: 0 }), []);

  const avgCph = statsAll.cph / CASES_DATA.length;
  const avgTth = statsAll.tth / CASES_DATA.length;

  const downloadPDF = useCallback((c: CaseItem) => {
    const content = `Кейс: ${c.title}\n\nРезюме: ${c.summary}\n\nДо: ${(c.before || []).join(', ')}\nПосле: ${(c.after || []).join(', ')}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (c.title.replace(/\s+/g, '_')) + '.txt';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }, []);

  const shareCase = useCallback(async (c: CaseItem) => {
    const url = caseUrl(c.id);
    if (navigator.share) {
      try {
        await navigator.share({ title: c.title, text: c.summary || '', url });
      } catch (error) {
      }
    } else {
      await navigator.clipboard.writeText(url);
      alert('Ссылка скопирована.');
    }
  }, []);

  const modalCase = useMemo(() => CASES_DATA.find(c => c.id === modalCaseId), [modalCaseId]);

  const CaseCard: React.FC<{ item: CaseItem }> = ({ item }) => (
    <article className="card rounded-2xl overflow-hidden flex flex-col w-full">
      <img src={item.cover} alt="" className="w-full h-44 object-cover" onError={(e) => e.currentTarget.src = 'https://placehold.co/800x480?text=Case'} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold break-words">{item.title}</h3>
            <div className="mt-1 text-xs flex flex-wrap gap-2">
              <span className="tag">{item.service}</span>
              <span className="tag">{item.industry}</span>
              <span className="tag">{item.size}</span>
              <span className="tag">{item.year}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1 flex-shrink-0">
            {(item.badges || []).map(b => <span key={b} className={`badge ${badgeClassName(b)}`}>{b}</span>)}
          </div>
        </div>

        <p className="text-sm text-gray-200 mt-3 line-clamp-3">{item.summary || ''}</p>

        <div className="mt-3 grid grid-cols-4 gap-1 text-center text-xs">
          <div className="card rounded-lg p-1.5">
            <div className="text-gray-300">Закрытия</div><div className="text-base font-bold">{item.result.hires || 0}</div>
          </div>
          <div className="card rounded-lg p-1.5">
            <div className="text-gray-300">CPH</div><div className="text-base font-bold">{fmt(item.result.cph || 0)}</div>
          </div>
          <div className="card rounded-lg p-1.5">
            <div className="text-gray-300">TTH</div><div className="text-base font-bold">{fmt(item.result.tth || 0)}</div>
          </div>
          <div className="card rounded-lg p-1.5">
            <div className="text-gray-300">Явка</div><div className="text-base font-bold">{item.result.show || 0}%</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2 flex-wrap">
            <button className="btn btn-ghost text-sm p-2" onClick={() => shareCase(item)}>Поделиться</button>
            <button className="btn btn-ghost text-sm p-2" onClick={() => downloadPDF(item)}>Скачать PDF (txt)</button>
          </div>
          <button className="btn btn-accent text-sm flex-shrink-0" onClick={() => openModal(item.id)}>Подробнее</button>
        </div>
      </div>
    </article>
  );

  return (
    <>
      <Head>
        <title>INTERIM — Кейсы</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />
        <style>
          {`
            :root{ --accent:#FFC300; --card:rgba(255,255,255,.08); --chip:rgba(255,255,255,.14) }
            .card{background:var(--card);}
            .chip{background:var(--chip)}
            .btn{border-radius:.75rem;padding:.7rem 1rem;font-weight:700}
            .btn-accent{background:var(--accent);color:#102a43}
            .btn-ghost{background:rgba(255,255,255,.10)}
            .btn-ghost:hover{background:rgba(255,255,255,.20)}
            .tag{background:rgba(255,255,255,.12);padding:.25rem .6rem;border-radius:999px;font-size:.75rem}
            .badge{padding:.2rem .5rem;border-radius:.5rem;font-size:.7rem}
            .badge-top{background:rgba(34,197,94,.25);color:#bbf7d0}
            .badge-awd{background:rgba(234,179,8,.25);color:#fde68a}
            .prose li { color: #d1d5db; }
            /* Добавлено: Базовый сброс для предотвращения горизонтального скролла на <body> */
            body { overflow-x: hidden; } 
          `}
        </style>
      </Head>

      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen overflow-x-hidden">
        <header className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}>
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <a href="#list" className="chip px-3 py-1 rounded-full hover:bg-white/25">Все кейсы</a>
              <a href="#filters" className="chip px-3 py-1 rounded-full hover:bg-white/25">Фильтры</a>
              <a href="#cta" className="chip px-3 py-1 rounded-full hover:bg-white/25">Обсудить проект</a>
            </nav>

            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Кейсы INTERIM</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Реальные результаты по массовому подбору, аутсорсингу и аутстаффингу: цифры до/после, KPI и сроки.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#list" className="btn btn-ghost">Смотреть кейсы</a>
              <a href="#cta" className="btn btn-accent">Получить такой же результат</a>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <section id="filters" aria-label="Фильтры кейсов" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Найти подходящий кейс</h2>

            <div className="grid gap-3 grid-cols-1 md:grid-cols-6">
              <input
                id="q"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2 w-full"
                placeholder="Поиск по названию/описанию…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <select id="svc" className="rounded-xl px-4 py-3 text-gray-900 w-full" value={service} onChange={e => setService(e.target.value)}>
                <option value="">Услуга: все</option>
                <option>Подбор (RPO)</option><option>Аутсорсинг</option><option>Аутстаффинг</option>
              </select>
              <select
                id="ind"
                className="rounded-xl px-4 py-3 text-gray-900 w-full"
                value={industry}
                onChange={e => setIndustry(e.target.value)}
              >
                <option value="">Индустрия: все</option>
                <option>IT-сфера и Интернет</option>
                <option>Агрономия</option>
                <option>Администрация</option>
                <option>Культура и искусство</option>
                <option>Педагогика</option>
                <option>Строительство и ремонт</option>
                <option>Сфера обслуживания</option>
                <option>Сфера промышленности</option>
                <option>Торговля и маркетинг</option>
                <option>Транспорт и логистика</option>
                <option>Уход и здоровье</option>
              </select>
              <select id="size" className="rounded-xl px-4 py-3 text-gray-900 w-full" value={size} onChange={e => setSize(e.target.value)}>
                <option value="">Размер компании</option>
                <option>Стартап</option><option>Средний бизнес</option><option>Крупная компания</option>
              </select>

              <div className="rounded-xl px-4 py-3 card flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:col-span-2 w-full hover:scale-100 transition-none">
                <label className="text-sm text-gray-200 flex-shrink-0">Годы</label>
                <input
                  id="y1"
                  type="range"
                  min="2025"
                  max="2026"
                  value={yearMin}
                  onChange={e => setYearMin(+e.target.value)}
                  className="flex-1 w-full"
                />
                <span id="y1v" className="text-sm flex-shrink-0">{yearMin}</span>
                <input
                  id="y2"
                  type="range"
                  min="2025"
                  max="2026"
                  value={yearMax}
                  onChange={e => setYearMax(+e.target.value)}
                  className="flex-1 w-full"
                />
                <span id="y2v" className="text-sm flex-shrink-0">{yearMax}</span>
              </div>
            </div>

            <div className="mt-3 grid gap-3 grid-cols-1 md:grid-cols-3">
              <select id="sort" className="rounded-xl px-4 py-3 text-gray-900 w-full" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="date">Сортировать: по дате ↓</option>
                <option value="impact">по результату (закрытия/экономия)</option>
                <option value="title">по названию</option>
              </select>
              <label className="card rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer hover:scale-100 w-full">
                <input
                  id="onlyTop"
                  type="checkbox"
                  className="w-5 h-5"
                  checked={onlyTop}
                  onChange={e => setOnlyTop(e.target.checked)}
                />
                <span className="text-sm text-gray-200">Только отмеченные (Top/Award)</span>
              </label>

              <div
                id="stats"
                className="card rounded-xl px-4 py-3 text-sm text-gray-200 hover:scale-100 w-full"
              >
                Суммарно: закрытий {statsAll.hires} • сред. CPH {fmt(avgCph)} • сред. TTH {fmt(avgTth)}
              </div>
            </div>
          </section>

          <section id="list" aria-label="Список кейсов" className="scroll-mt-32">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <h2 className="text-3xl md:text-4xl font-bold">Кейсы</h2>
              <span id="count" className="text-sm text-gray-300">Найдено кейсов: {filteredItems.length}</span>
            </div>

            <div id="grid" className="mt-6 grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {paginatedItems.map(item => <CaseCard key={item.id} item={item} />)}
            </div>
            <p id="empty" className={filteredItems.length === 0 ? 'text-gray-300' : 'hidden'}>Ничего не найдено — измените фильтры.</p>
            {filteredItems.length > 0 && (
              <div id="pager" className="mt-6 flex flex-wrap items-center gap-2 justify-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(i => (
                  <button
                    key={i}
                    className={`px-3 py-2 rounded-lg ${i === page ? 'btn-accent' : 'btn-ghost'}`}
                    onClick={() => setPage(i)}
                  >
                    {i}
                  </button>
                ))}
              </div>
            )}
          </section>

          <section
            id="cta"
            className="card rounded-2xl p-6 scroll-mt-32 transform-none hover:scale-100"
          >
            <h3 className="text-2xl font-bold">Хотите похожий результат?</h3>
            <p className="text-sm text-gray-200 mt-1">Оставьте контакты — подберём решение и расчёт KPI за 24 часа.</p>
            <LeadForm source="Cases Page CTA" />
          </section>
        </main>
      </div>

      <div
        id="modal"
        className={`fixed inset-0 bg-blue-900/75 flex items-center justify-center z-50 transition-opacity duration-300 p-4 ${isModalOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`} // Добавлено p-4 для отступа на мобильных
        aria-hidden={!isModalOpen}
        role="dialog"
        aria-modal="true"
        aria-label={modalCase?.title || "Детали кейса"}
        onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
      >
        {modalCase && (
          <div className="bg-blue-800 text-white rounded-2xl p-6 max-w-4xl w-full mx-auto relative overflow-y-auto max-h-[90vh]">
            <button
              id="close"
              className="absolute right-4 top-4 px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 text-xl z-10"
              aria-label="Закрыть модальное окно"
              onClick={closeModal}>
              ×
            </button>
            <div id="mc" className="mt-2">
              <article id={`case-${modalCase.id}`}>
                <img src={modalCase.cover} className="rounded-xl w-full h-56 object-cover" alt={`Обложка кейса ${modalCase.title}`} onError={(e) => e.currentTarget.src = 'https://placehold.co/800x480?text=Case'} />
                <h2 className="text-2xl font-bold mt-4">{modalCase.title}</h2>
                <div className="mt-2 text-xs flex flex-wrap gap-2">
                  <span className="tag text-white bg-white/20">{modalCase.service}</span>
                  <span className="tag text-white bg-white/20">{modalCase.industry}</span>
                  <span className="tag text-white bg-white/20">{modalCase.size}</span>
                  <span className="tag text-white bg-white/20">{modalCase.year}</span>
                </div>

                <div className="mt-4 grid gap-4 grid-cols-1 md:grid-cols-2">
                  <div className="card rounded-xl p-4">
                    <h3 className="font-semibold mb-2">До</h3>
                    <ul className="list-disc ml-5 text-sm text-gray-200">
                      {(modalCase.before || []).map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                  </div>
                  <div className="card rounded-xl p-4">
                    <h3 className="font-semibold mb-2">После</h3>
                    <ul className="list-disc ml-5 text-sm text-gray-200">
                      {(modalCase.after || []).map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-1 text-center text-xs">
                  <div className="card rounded-lg p-1.5">
                    <div className="text-gray-300">Закрытия</div><div className="text-base font-bold">{modalCase.result.hires || 0}</div>
                  </div>
                  <div className="card rounded-lg p-1.5">
                    <div className="text-gray-300">CPH</div><div className="text-base font-bold">{fmt(modalCase.result.cph || 0)}</div>
                  </div>
                  <div className="card rounded-lg p-1.5">
                    <div className="text-gray-300">TTH</div><div className="text-base font-bold">{fmt(modalCase.result.tth || 0)}</div>
                  </div>
                  <div className="card rounded-lg p-1.5">
                    <div className="text-gray-300">Явка</div><div className="text-base font-bold">{modalCase.result.show || 0}%</div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(modalCase.kpis || []).map((t, idx) => <span key={idx} className="tag text-white bg-white/20">{t}</span>)}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button className="btn btn-ghost" onClick={() => downloadPDF(modalCase)}>Скачать PDF (txt)</button>
                  <button className="btn btn-accent" onClick={() => alert('Оставьте контакты в форме ниже — свяжемся!')}>Связаться</button>
                </div>
              </article>
            </div>
          </div>
        )}
      </div>
    </>
  );
}